import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Facebook, Calendar, Send, Clock, CheckCircle2, XCircle, ExternalLink, Trash2, RefreshCw, Instagram, KeyRound, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";
import PublishingPauseBanner from "../components/PublishingPauseBanner";

export default function FacebookPagesModule() {
  const [health, setHealth] = useState(null);
  const [presets, setPresets] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [crossPostIG, setCrossPostIG] = useState(false);
  const [igHealth, setIgHealth] = useState(null);

  // ── Reconnexion Meta sécurisée ──
  const [showReconnect, setShowReconnect] = useState(false);
  const [rcAppId, setRcAppId] = useState("990999397233303");
  const [rcSecret, setRcSecret] = useState("");
  const [rcToken, setRcToken] = useState("");
  const [rcPages, setRcPages] = useState(null); // liste si plusieurs Pages
  const [rcBusy, setRcBusy] = useState(false);
  const [sysToken, setSysToken] = useState("");
  const [sysPageId, setSysPageId] = useState("372288592638280");
  const [sysBusy, setSysBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, p, l, ig] = await Promise.all([
        api.get("/facebook/health"),
        api.get("/facebook/presets"),
        api.get("/facebook/posts?limit=30"),
        api.get("/instagram/health").catch(() => ({ data: { configured: false } })),
      ]);
      setHealth(h.data);
      setPresets(p.data.presets || []);
      setPosts(l.data.posts || []);
      setIgHealth(ig.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur chargement");
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const publish = async (slug, scheduled = false) => {
    if (scheduled && !scheduledTime) {
      toast.error("Choisis une date/heure de programmation");
      return;
    }
    setLoading(true);
    const preset = presets.find((p) => p.slug === slug);
    try {
      const payload = scheduled ? { scheduled_time: new Date(scheduledTime).toISOString() } : null;
      if (imageUrl) (payload || {}).image_url = imageUrl;
      // FB
      await api.post(`/facebook/post/preset/${slug}`, payload);
      // IG cross-post (requires image_url)
      if (crossPostIG && imageUrl && preset) {
        try {
          if (scheduled) {
            await api.post(`/instagram/post/schedule`, {
              image_url: imageUrl,
              caption: preset.message,
              scheduled_time: new Date(scheduledTime).toISOString(),
              label: slug,
            });
          } else {
            await api.post(`/instagram/post`, {
              image_url: imageUrl,
              caption: preset.message,
              label: slug,
            });
          }
          toast.success("Publié sur Facebook + Instagram ✓");
        } catch (igErr) {
          toast.warning("FB ok mais IG échec : " + (igErr.response?.data?.detail || "erreur"));
        }
      } else {
        toast.success(scheduled ? "Post programmé sur Facebook ✓" : "Post publié sur Facebook ✓");
      }
      setSelectedPreset(null);
      setImageUrl("");
      setScheduledTime("");
      setCrossPostIG(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur publication");
    }
    setLoading(false);
  };

  const cancel = async (id) => {
    if (!confirm("Annuler ce post programmé ?")) return;
    try {
      await api.delete(`/facebook/post/${id}`);
      toast.success("Post annulé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const reconnect = async (pageId = null) => {
    if (!rcSecret.trim() || !rcToken.trim() || !rcAppId.trim()) {
      toast.error("Remplis App ID, App Secret et le token court.");
      return;
    }
    setRcBusy(true);
    try {
      const res = await api.post("/facebook/reconnect", {
        app_id: rcAppId.trim(),
        app_secret: rcSecret.trim(),
        short_lived_token: rcToken.trim(),
        page_id: pageId || undefined,
      });
      const d = res.data;
      toast.success(`Reconnecté ✓ Page « ${d.page?.name} »${d.instagram_linked ? ` + IG @${d.instagram_username}` : ""}`);
      setRcSecret(""); setRcToken(""); setRcPages(null); setShowReconnect(false);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Plusieurs Pages → l'utilisateur doit en choisir une
      if (detail && typeof detail === "object" && detail.need_page_choice) {
        setRcPages(detail.pages || []);
        toast.info("Plusieurs Pages détectées — choisis la bonne ci-dessous.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Échec de la reconnexion Meta.");
      }
    }
    setRcBusy(false);
  };

  const connectSystemToken = async () => {
    if (!sysToken.trim()) {
      toast.error("Colle ton jeton « Utilisateur système ».");
      return;
    }
    setSysBusy(true);
    try {
      const res = await api.post("/facebook/connect-system-token", {
        token: sysToken.trim(),
        page_id: sysPageId.trim() || undefined,
      });
      const d = res.data;
      toast.success(`Connecté ✓ Page « ${d.page?.name} »${d.instagram_linked ? ` + IG @${d.instagram_username}` : ""} — jeton qui n'expire jamais 🔒`);
      setSysToken(""); setShowReconnect(false);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.need_page_choice) {
        toast.info("Plusieurs Pages détectées — indique le Page ID exact ci-dessus.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Échec de la connexion.");
      }
    }
    setSysBusy(false);
  };


  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="facebook-pages-module">
      <PublishingPauseBanner />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-amber-300 flex items-center gap-2">
            <Facebook className="w-6 h-6" /> Publication Facebook
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Publie ou programme les posts commerciaux du dojo en 1 clic.
          </p>
        </div>
        <button onClick={load} className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-xs flex items-center gap-1" data-testid="refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" /> Rafraîchir
        </button>
      </div>

      {/* ── Reconnexion Meta sécurisée ── */}
      <div className="mb-6 rounded-lg border border-amber-800/40 bg-zinc-900/60 overflow-hidden" data-testid="meta-reconnect-panel">
        <button
          onClick={() => setShowReconnect((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-900"
          data-testid="toggle-reconnect-btn"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-amber-300">
            <KeyRound className="w-4 h-4" /> Reconnexion Meta sécurisée (nouvel app)
          </span>
          {showReconnect ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
        </button>

        {showReconnect && (
          <div className="px-4 pb-4 border-t border-zinc-800 pt-4" data-testid="reconnect-form">
            {/* ✅ MÉTHODE RECOMMANDÉE : jeton Utilisateur système (n'expire jamais) */}
            <div className="mb-5 p-4 rounded bg-emerald-950/30 border border-emerald-700/50" data-testid="system-token-box">
              <p className="font-semibold text-emerald-200 mb-1 flex items-center gap-2">✅ Méthode recommandée — Jeton « Utilisateur système » (n'expire JAMAIS)</p>
              <p className="text-[11px] text-zinc-300 mb-3 leading-relaxed">
                Dans Meta Business Settings → Utilisateurs système → « Kaizen API » → <b>Generate token</b> (app « Impact Kaizen Social », expiration <b>Never</b>, permissions pages + instagram). Colle le jeton ici — pas besoin d'App ID/Secret.
              </p>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className="text-xs text-zinc-400 block mb-1">Jeton longue durée (Utilisateur système)</label>
                  <input
                    type="password"
                    value={sysToken}
                    onChange={(e) => setSysToken(e.target.value)}
                    placeholder="EAA••••••••"
                    autoComplete="off"
                    className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-zinc-100 font-mono"
                    data-testid="sys-token"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 block mb-1">Page ID</label>
                  <input
                    value={sysPageId}
                    onChange={(e) => setSysPageId(e.target.value)}
                    className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-zinc-100 font-mono"
                    data-testid="sys-page-id"
                  />
                </div>
              </div>
              <button
                onClick={connectSystemToken}
                disabled={sysBusy}
                className="mt-3 px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                data-testid="sys-submit-btn"
              >
                <ShieldCheck className="w-4 h-4" /> {sysBusy ? "Connexion…" : "Connecter (jeton longue durée)"}
              </button>
            </div>

            <p className="text-[11px] text-zinc-500 mb-2 uppercase tracking-wide">— ou méthode classique (token court via Graph API Explorer) —</p>
            <div className="mb-4 p-3 rounded bg-blue-950/30 border border-blue-800/40 text-xs text-blue-100 leading-relaxed">
              <p className="font-semibold text-blue-200 mb-1">📋 Comment obtenir ton token court (1 min) :</p>
              <ol className="list-decimal ml-4 space-y-1 text-zinc-300">
                <li>Va sur <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer" className="text-amber-400 underline">Graph API Explorer</a></li>
                <li>En haut à droite, dans <b>« Meta App »</b>, sélectionne <b>« Impact Kaizen Social »</b> (ID 990999397233303) — <span className="text-red-300">PAS l'ancien app banni</span></li>
                <li>Clique <b>« Add a Permission »</b> et coche : <code className="text-amber-300">pages_show_list, pages_manage_posts, pages_read_engagement, instagram_basic, instagram_content_publish, business_management</code></li>
                <li>Clique <b>« Generate Access Token »</b> → autorise → copie le token</li>
                <li>Colle-le ci-dessous. Aucun secret ne passe par le chat. 🔒</li>
              </ol>
            </div>

            <div className="grid md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="text-xs text-zinc-400 block mb-1">App ID</label>
                <input
                  value={rcAppId}
                  onChange={(e) => setRcAppId(e.target.value)}
                  className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-zinc-100 font-mono"
                  data-testid="rc-app-id"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 block mb-1">App Secret</label>
                <input
                  type="password"
                  value={rcSecret}
                  onChange={(e) => setRcSecret(e.target.value)}
                  placeholder="••••••••••••••••"
                  autoComplete="off"
                  className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-zinc-100 font-mono"
                  data-testid="rc-app-secret"
                />
                <p className="text-[10px] text-zinc-500 mt-1">App Dashboard → Paramètres → De base</p>
              </div>
              <div>
                <label className="text-xs text-zinc-400 block mb-1">Token utilisateur court</label>
                <input
                  type="password"
                  value={rcToken}
                  onChange={(e) => setRcToken(e.target.value)}
                  placeholder="EAA••••••••"
                  autoComplete="off"
                  className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-zinc-100 font-mono"
                  data-testid="rc-token"
                />
                <p className="text-[10px] text-zinc-500 mt-1">Depuis Graph API Explorer (nouvel app)</p>
              </div>
            </div>

            {/* Choix de Page si plusieurs */}
            {rcPages && rcPages.length > 0 && (
              <div className="mb-3 p-3 rounded bg-amber-950/30 border border-amber-800/40" data-testid="rc-page-choice">
                <p className="text-xs text-amber-200 mb-2 font-semibold">Plusieurs Pages détectées — choisis ta Page :</p>
                <div className="space-y-2">
                  {rcPages.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => reconnect(p.id)}
                      disabled={rcBusy}
                      className="w-full text-left px-3 py-2 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-sm text-zinc-200 flex items-center justify-between disabled:opacity-50"
                      data-testid={`rc-page-${p.id}`}
                    >
                      <span>{p.name} <span className="text-zinc-500 text-xs">· {p.id}</span></span>
                      {p.has_instagram && <Instagram className="w-3.5 h-3.5 text-pink-400" />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => reconnect(null)}
              disabled={rcBusy}
              className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-zinc-950 text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
              data-testid="rc-submit-btn"
            >
              <ShieldCheck className="w-4 h-4" /> {rcBusy ? "Connexion en cours…" : "Reconnecter Meta"}
            </button>
          </div>
        )}
      </div>

      {/* Status */}
      {health && !health.configured && (
        <div className="mb-6 p-5 rounded-lg bg-red-950/40 border border-red-800/60" data-testid="not-configured">
          <h3 className="text-red-300 font-semibold mb-2">⚠️ Facebook API non configuré</h3>
          <p className="text-sm text-zinc-300 mb-3">
            Ajoute les variables suivantes dans <code className="text-amber-400">/app/backend/.env</code> :
          </p>
          <ul className="text-xs font-mono text-zinc-400 space-y-1 ml-4">
            <li>• FACEBOOK_PAGE_ID</li>
            <li>• FACEBOOK_PAGE_ACCESS_TOKEN</li>
          </ul>
          <p className="text-xs text-zinc-500 mt-3">
            Guide complet : <code className="text-amber-400">/app/memory/FACEBOOK_API_SETUP_GUIDE.md</code>
          </p>
        </div>
      )}

      {health?.configured && health?.page && (
        <div className="mb-6 p-4 rounded-lg bg-emerald-950/30 border border-emerald-800/40 flex items-center gap-3" data-testid="health-ok">
          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          <div className="flex-1">
            <div className="text-sm text-emerald-200 font-semibold">{health.page.name}</div>
            <div className="text-xs text-zinc-500">
              {health.page.category} · {health.page.fan_count || 0} fans · Page ID {health.page.id} · API {health.api_version}
            </div>
          </div>
        </div>
      )}

      {/* Presets */}
      <div className="mb-6">
        <h2 className="text-sm font-semibold text-zinc-200 mb-3 uppercase tracking-wider">📌 Posts commerciaux pré-définis</h2>
        <div className="grid md:grid-cols-3 gap-3">
          {presets.map((p) => (
            <div
              key={p.slug}
              className={`rounded-lg border p-4 cursor-pointer transition ${
                selectedPreset === p.slug ? "border-amber-500 bg-amber-950/30" : "border-zinc-800 bg-zinc-900/50 hover:border-zinc-600"
              }`}
              onClick={() => setSelectedPreset(p.slug)}
              data-testid={`preset-${p.slug}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="text-sm font-semibold text-amber-200">{p.label}</div>
                <Clock className="w-3.5 h-3.5 text-zinc-500" />
              </div>
              <div className="text-[10px] uppercase text-zinc-500 mb-2">
                {p.default_day} · {p.default_time}
              </div>
              <p className="text-xs text-zinc-400 whitespace-pre-line line-clamp-6">{p.message.slice(0, 250)}...</p>
            </div>
          ))}
        </div>
      </div>

      {/* IG Status indicator */}
      {igHealth && igHealth.linked && igHealth.account && (
        <div className="mb-3 p-3 rounded-lg bg-pink-950/30 border border-pink-800/40 flex items-center gap-3" data-testid="ig-status">
          <Instagram className="w-5 h-5 text-pink-400" />
          <div className="flex-1 text-xs">
            <span className="text-pink-200 font-semibold">@{igHealth.account.username}</span>
            <span className="text-zinc-500"> · {igHealth.account.followers_count || 0} abonnés · {igHealth.account.media_count || 0} posts</span>
          </div>
          <span className="text-[10px] uppercase tracking-wider text-pink-400/80">Compte IG lié ✓</span>
        </div>
      )}
      {igHealth && igHealth.configured && !igHealth.linked && (
        <div className="mb-3 p-3 rounded-lg bg-amber-950/30 border border-amber-800/40 text-xs text-amber-200" data-testid="ig-not-linked">
          ⚠️ Aucun compte Instagram Business lié à la Page FB. Va dans Meta Business Suite → Linked Accounts pour activer le cross-post.
        </div>
      )}

      {/* Action panel — when preset selected */}
      {selectedPreset && (
        <div className="mb-6 p-5 rounded-lg bg-zinc-900/70 border border-amber-700/40" data-testid="action-panel">
          <h3 className="text-amber-300 font-semibold mb-3">
            Publier : {presets.find((p) => p.slug === selectedPreset)?.label}
          </h3>

          <div className="grid md:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Image URL (optionnel · OBLIGATOIRE pour cross-post IG)</label>
              <input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://exemple.com/photo.jpg"
                className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-zinc-100"
                data-testid="image-url-input"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Date/heure (vide = immédiat)</label>
              <input
                type="datetime-local"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-zinc-100"
                data-testid="scheduled-input"
              />
            </div>
          </div>

          {/* Cross-post Instagram toggle */}
          {igHealth?.linked && (
            <label className="flex items-center gap-2 mb-3 cursor-pointer" data-testid="cross-post-ig-toggle">
              <input
                type="checkbox"
                checked={crossPostIG}
                onChange={(e) => setCrossPostIG(e.target.checked)}
                disabled={!imageUrl}
                className="rounded"
              />
              <Instagram className="w-4 h-4 text-pink-400" />
              <span className="text-sm text-zinc-300">
                Cross-poster aussi sur Instagram <span className="text-pink-300">(@{igHealth.account?.username})</span>
                {!imageUrl && <span className="text-zinc-500 text-xs"> — image_url requise</span>}
              </span>
            </label>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => publish(selectedPreset, false)}
              disabled={loading}
              className="px-4 py-2 rounded bg-emerald-700 hover:bg-emerald-600 text-emerald-50 text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
              data-testid="publish-now-btn"
            >
              <Send className="w-4 h-4" /> Publier MAINTENANT
            </button>
            <button
              onClick={() => publish(selectedPreset, true)}
              disabled={loading || !scheduledTime}
              className="px-4 py-2 rounded bg-amber-700 hover:bg-amber-600 text-amber-50 text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
              data-testid="schedule-btn"
            >
              <Calendar className="w-4 h-4" /> Programmer
            </button>
            <button
              onClick={() => setSelectedPreset(null)}
              className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm"
              data-testid="cancel-btn"
            >
              Annuler
            </button>
          </div>
          <p className="text-xs text-zinc-500 mt-3">
            ℹ️ Heure interprétée en heure de Val-d'Or (America/Toronto). Min 10 min dans le futur · max 75 jours.
          </p>
        </div>
      )}

      {/* Historique */}
      <div className="rounded-xl bg-zinc-900/60 border border-zinc-800 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800">
          <h2 className="text-sm font-semibold text-zinc-200">📜 Historique ({posts.length})</h2>
        </div>
        {posts.length === 0 && (
          <div className="p-8 text-center text-zinc-500 text-sm">Aucun post encore.</div>
        )}
        {posts.length > 0 && (
          <div className="max-h-[500px] overflow-y-auto">
            {posts.map((p) => (
              <div key={p.id} className="px-4 py-3 border-t border-zinc-800 flex items-start gap-3" data-testid={`fb-post-${p.id}`}>
                <div className="shrink-0 mt-1">
                  {p.status === "published" && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                  {p.status === "scheduled" && <Clock className="w-4 h-4 text-amber-400" />}
                  {p.status === "cancelled" && <XCircle className="w-4 h-4 text-red-400" />}
                  {p.status === "failed" && <XCircle className="w-4 h-4 text-red-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-zinc-500">
                    {p.label && <span className="text-amber-400">{p.label} · </span>}
                    {p.status} · {new Date(p.created_at).toLocaleString("fr-CA")}
                    {p.scheduled_time && <span className="text-amber-300"> · prévu {new Date(p.scheduled_time).toLocaleString("fr-CA")}</span>}
                  </div>
                  <p className="text-sm text-zinc-300 mt-1 line-clamp-3 whitespace-pre-line">{p.message?.slice(0, 250)}{p.message?.length > 250 ? "…" : ""}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {p.facebook_post_id && (
                    <a
                      href={`https://www.facebook.com/${p.facebook_post_id}`}
                      target="_blank" rel="noreferrer"
                      className="text-zinc-400 hover:text-amber-400" title="Voir sur Facebook"
                      data-testid={`view-fb-${p.id}`}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  {p.status === "scheduled" && (
                    <button onClick={() => cancel(p.id)} className="text-zinc-400 hover:text-red-400" title="Annuler" data-testid={`cancel-fb-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
